/* eslint-disable jsx-a11y/tabindex-no-positive */
import MultiSelectCheckbox, {
  TmenuItem,
} from '@components/UI/Form/MultiSelectCheckbox';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Popover,
  Tab,
  Tabs,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Facebook,
  InfoOutlined,
  Telegram,
  WhatsApp,
} from '@material-ui/icons/';
import { TabContext, TabPanel } from '@material-ui/lab';
import {
  BotAccountItem,
  fetchBotAccounts,
  fetchBotButtons,
  resetFilter,
  updateBotButtons,
} from '@reducers/botAccounts';
import {
  getBotButtons,
  getBotList,
  selectBotAccounts,
  selectBotAccountsRaw,
} from '@selectors/botAccounts';
import { parseMessage } from '@src/locales/intl';
import { BotAccountStates, MessagingProvider } from '@src/model/frontendmodel';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const lblAllProviders = parseMessage('Bot.Channels.All');
const lblAll = parseMessage('Bot.Provider.All');
const lblButtons = parseMessage('Bot.Tab.Buttons');
const lblList = parseMessage('Bot.Tab.List');
const helpText = parseMessage('Bot.Tab.List.Tooltip');
const TOTAL_PROVIDERS = 3;

type BotButtonsProps = {
  anchorEl: Element | null;
  handleClose: () => void;
};

const cssTabPanel = { margin: '10px', padding: 0 };
const px = { padding: '7px 0' };
const sxTab = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gridGap: '10px',
};

const useStyles = makeStyles({
  paper: {
    minWidth: '300px',
    padding: '0.5rem',
    boxShadow:
      '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
  },
});

const BotButtons: React.FC<BotButtonsProps> = ({ anchorEl, handleClose }) => {
  const lblAllChannels = parseMessage('Analytics.providerALL');
  const lblWpp = parseMessage('Analytics.providerWA');
  const lblFb = parseMessage('Analytics.providerFB');
  const lblTelegram = parseMessage('Analytics.providerTG');
  const customStyle = useStyles();
  const dispatch = useDispatch();
  const { data } = useSelector(selectBotAccounts);
  const botButtonsData = useSelector(getBotButtons);
  const botListData = useSelector(getBotList);
  const { pathname } = useLocation();
  const [configButtons, setConfigButtons] = useState(botButtonsData);
  const [configList, setConfigList] = useState(botListData);
  const messagingAccounts = useSelector(selectBotAccountsRaw);
  const [tab, setTab] = useState('0');

  const setBotButtons = (
    provider: MessagingProvider | null,
    userName: string,
    allBotAccounts: boolean
  ) => {
    const modulename = pathname.split('/')[2];
    let providersSelected = configButtons.forceEnabledButtonsForProviders;
    let botAccountSettings = configButtons.buttonsBotAccountSettings;
    let allBotButtons = allBotAccounts;

    if (allBotButtons) {
      providersSelected = [];
      botAccountSettings = [];
    }

    if (provider) {
      allBotButtons = false;

      const isCheckedProvider = providersSelected.some(
        item => item === provider
      );

      if (userName === lblAllProviders) {
        providersSelected = isCheckedProvider
          ? providersSelected.filter(item => item !== provider)
          : [...providersSelected, provider];

        botAccountSettings = botAccountSettings.filter(
          item => item.provider !== provider
        );
      } else if (isCheckedProvider) {
        const channelsByProvider = getAccountByProvider(provider)
          .filter(item => item.name !== userName)
          .map(item => ({
            buttonsEnabled: true,
            userName: item.name,
            provider: item.key,
          }));
        botAccountSettings = [...botAccountSettings, ...channelsByProvider];

        providersSelected = providersSelected.filter(item => item !== provider);
      } else {
        const channelsByProvider = getAccountByProvider(provider);

        const isChannelSelected = botAccountSettings.some(
          item => item.userName === userName && item.provider === provider
        );

        if (isChannelSelected) {
          botAccountSettings = botAccountSettings.filter(
            item => item.provider === provider && item.userName !== userName
          );
        } else {
          const channelsSelected = botAccountSettings.filter(
            item => item.provider === provider
          );

          if (channelsByProvider.length !== channelsSelected.length + 1) {
            botAccountSettings = [
              ...botAccountSettings,
              {
                provider,
                userName,
                buttonsEnabled: true,
              },
            ];
          } else {
            providersSelected = [...providersSelected, provider];
            botAccountSettings = botAccountSettings.filter(
              item => item.provider !== provider
            );
          }
        }
      }
    }

    const allSelected = providersSelected.length === TOTAL_PROVIDERS;
    if (allSelected) {
      providersSelected = [];
      allBotButtons = true;
    }

    const botButtons = {
      forceEnabledButtonsForAllBotAccounts: allBotButtons,
      forceEnabledButtonsForProviders: providersSelected,
      buttonsBotAccountSettings: botAccountSettings,
    };

    setConfigButtons(botButtons);
    const messaging = {
      buttonsSettings: botButtons,
      interactiveListSettings: configList,
    };
    dispatch(updateBotButtons({ messaging, modulename }));
  };

  const setBotList = (selectedItem: TmenuItem, checked: boolean) => {
    const { name, id } = selectedItem;
    const modulename = pathname.split('/')[2];
    const provider = MessagingProvider.WHATSAPPB2CHAT;
    let botListSettings = configList.interactiveListBotAccountSettings;
    let forceEnabled = configList.forceEnabledListForAllBotAccounts;

    if (name !== lblAllProviders) {
      const channelsByProvider = getAccountByProvider(provider);

      if (checked) {
        botListSettings = [
          ...botListSettings,
          {
            provider,
            userName: id || '',
            buttonsEnabled: true,
          },
        ];
      } else if (!forceEnabled && !checked) {
        botListSettings = botListSettings.filter(item => item.userName !== id);
      } else {
        botListSettings = [];
        channelsByProvider.forEach(item => {
          if (item.name !== name) {
            botListSettings = [
              ...botListSettings,
              {
                provider,
                userName: item?.id || '',
                buttonsEnabled: true,
              },
            ];
          }
        });
      }

      const allChecked = channelsByProvider.length === botListSettings.length;
      forceEnabled = allChecked;
      botListSettings = allChecked ? [] : botListSettings;
    } else {
      forceEnabled = !forceEnabled;
      botListSettings = [];
    }

    const botList = {
      forceEnabledListForAllBotAccounts: forceEnabled,
      interactiveListBotAccountSettings: botListSettings,
    };

    setConfigList(botList);
    const messaging = {
      buttonsSettings: configButtons,
      interactiveListSettings: botList,
    };

    dispatch(updateBotButtons({ messaging, modulename }));
  };

  const getAccountByProvider = (providerName: string) =>
    data
      .filter((item: BotAccountItem) => {
        const { botAccount, status } = item;
        return (
          status === BotAccountStates.ACTIVE &&
          botAccount.provider === providerName
        );
      })
      .map(item => {
        const { username, identifier, alias, provider } = item.botAccount;
        return {
          name: alias || identifier || username,
          key: provider,
          id: username,
        };
      });

  const onParseData = (provider: MessagingProvider) => [
    ...[{ name: lblAllProviders, key: provider }].concat(
      getAccountByProvider(provider)
    ),
  ];

  const defaultValue = configButtons.buttonsBotAccountSettings
    .map(item => item.userName)
    .concat(configButtons.forceEnabledButtonsForProviders);

  const defaultBotList = () => {
    const lines = getAccountByProvider(MessagingProvider.WHATSAPPB2CHAT);

    const getUserName = (userName: string) => {
      const line = lines.find(item => item.id === userName) || { name: '' };
      return line.name;
    };

    return [
      ...configList.interactiveListBotAccountSettings.map(item =>
        getUserName(item.userName)
      ),
      ...(configList.forceEnabledListForAllBotAccounts
        ? [MessagingProvider.WHATSAPPB2CHAT]
        : []),
    ];
  };

  const onChecked = (channel: TmenuItem) => {
    const hasProvider = configButtons.forceEnabledButtonsForProviders.some(
      item => item === channel.key
    );

    const hasUserName = configButtons.buttonsBotAccountSettings.some(
      item => item.userName === channel.name
    );

    return hasProvider || hasUserName;
  };

  const onCheckedList = (channel: TmenuItem) => {
    const forceEnabled = configList.forceEnabledListForAllBotAccounts;
    if (forceEnabled) return true;
    if (channel.name === lblAllProviders) return forceEnabled;

    const lines = getAccountByProvider(MessagingProvider.WHATSAPPB2CHAT);
    const getId = (userName: string) => {
      const line = lines.find(item => item.name === userName) || { id: '' };
      return line.id;
    };

    const hasUserName = configList.interactiveListBotAccountSettings.some(
      item => item.userName === getId(channel.name)
    );

    return hasUserName;
  };

  const onRenderValue = (provider: MessagingProvider) => {
    let count = 0;
    let result = '';

    onParseData(provider).forEach(channel => {
      const hasProvider = configButtons.forceEnabledButtonsForProviders.some(
        item => item === provider
      );

      if (hasProvider) {
        result = lblAll;
        return;
      }
      if (channel.key === provider && defaultValue.includes(channel.name))
        result = `${(count += 1)}+`;
    });

    return result;
  };

  const onRenderList = () => {
    const provider = MessagingProvider.WHATSAPPB2CHAT;
    let count = 0;
    let result = '';

    onParseData(provider).forEach(channel => {
      const forceEnabled = configList.forceEnabledListForAllBotAccounts;

      if (forceEnabled) {
        result = lblAll;
        return;
      }
      if (defaultBotList().includes(channel.name)) result = `${(count += 1)}+`;
    });

    return result;
  };

  const handleSelected = (
    _event: React.ChangeEvent<HTMLInputElement>,
    _checked: boolean,
    selectedItem: TmenuItem
  ) => {
    const { key, name } = selectedItem;
    setBotButtons(
      key as MessagingProvider,
      name,
      configButtons.forceEnabledButtonsForAllBotAccounts
    );
  };

  const handleSelectedList = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
    selectedItem: TmenuItem
  ) => {
    setBotList(selectedItem, checked);
  };

  useEffect(() => {
    setConfigButtons(botButtonsData);
    setConfigList(botListData);
  }, [botButtonsData, botListData]);

  useEffect(() => {
    if (!messagingAccounts?.length)
      dispatch(fetchBotAccounts({ limit: 1000, offset: 0 }));

    const moduleName = pathname.split('/')[2];
    dispatch(fetchBotButtons(moduleName));

    return () => {
      dispatch(resetFilter());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderButtons = (
    <Grid container direction="column" spacing={1}>
      <Grid item style={px}>
        <MultiSelectCheckbox
          items={onParseData(MessagingProvider.WHATSAPPB2CHAT)}
          defaultValue={defaultValue}
          handleSelected={handleSelected}
          onChecked={onChecked}
          placeholder={lblWpp}
          icon={<WhatsApp fontSize="small" />}
          renderValue={onRenderValue(MessagingProvider.WHATSAPPB2CHAT)}
        />
      </Grid>
      <Grid item style={px}>
        <MultiSelectCheckbox
          items={onParseData(MessagingProvider.FACEBOOK)}
          defaultValue={defaultValue}
          handleSelected={handleSelected}
          onChecked={onChecked}
          placeholder={lblFb}
          icon={<Facebook fontSize="small" />}
          renderValue={onRenderValue(MessagingProvider.FACEBOOK)}
        />
      </Grid>
      <Grid item style={px}>
        <MultiSelectCheckbox
          items={onParseData(MessagingProvider.TELEGRAM)}
          defaultValue={defaultValue}
          handleSelected={handleSelected}
          onChecked={onChecked}
          placeholder={lblTelegram}
          icon={<Telegram fontSize="small" />}
          renderValue={onRenderValue(MessagingProvider.TELEGRAM)}
        />
      </Grid>
      <Grid item>
        <Box display="flex" justifyContent="flex-start">
          <FormControlLabel
            control={
              <Checkbox
                checked={configButtons.forceEnabledButtonsForAllBotAccounts}
                onChange={(_event, checked: boolean) => {
                  setBotButtons(null, lblAllChannels, checked);
                }}
                name="allChannels"
                color="primary"
              />
            }
            label={lblAllChannels}
          />
        </Box>
      </Grid>
    </Grid>
  );

  const renderWhatsappBot = (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <MultiSelectCheckbox
          items={onParseData(MessagingProvider.WHATSAPPB2CHAT)}
          defaultValue={defaultBotList()}
          handleSelected={handleSelectedList}
          onChecked={onCheckedList}
          placeholder={lblWpp}
          id="msc-botlist"
          icon={<WhatsApp fontSize="small" />}
          renderValue={onRenderList()}
        />
      </Grid>
    </Grid>
  );

  const renderTabText = (
    <Box sx={sxTab}>
      {lblList}
      <Tooltip title={helpText}>
        <InfoOutlined fontSize="small" />
      </Tooltip>
    </Box>
  );

  const onChangeTab = (newTab: string) => {
    setTab(newTab);
  };

  const tabProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
    value: `${index}`,
    tabIndex: index,
    style: { fontWeight: 600 },
  });

  const tabPanelProps = (index: number) => ({
    id: `simple-tabpanel-${index}`,
    'aria-labelledby': `simple-tab-${index}`,
    value: `${index}`,
    tabIndex: index,
  });

  const bgColorLeft = tab === '0' ? '#4C33FF' : '#D4D4D8';
  const bgColorRight = tab === '0' ? '#D4D4D8' : '#4C33FF';

  return (
    <Popover
      classes={{ paper: customStyle.paper }}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <TabContext value={tab}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => {
            onChangeTab(newValue);
          }}
          indicatorColor="primary"
          textColor="primary"
          style={{ padding: '10px' }}
          TabIndicatorProps={{
            style: {
              left: 0,
              width: '100%',
              background: `linear-gradient(to right, ${bgColorLeft} 50%, ${bgColorRight} 50%)`,
            },
          }}
        >
          <Tab label={lblButtons} {...tabProps(0)} />
          <Tab label={renderTabText} {...tabProps(1)} />
        </Tabs>
        <TabPanel {...tabPanelProps(0)} key="bot_buttons" style={cssTabPanel}>
          {renderButtons}
        </TabPanel>
        <TabPanel {...tabPanelProps(1)} key="bot_list" style={cssTabPanel}>
          {renderWhatsappBot}
        </TabPanel>
      </TabContext>
    </Popover>
  );
};

export default BotButtons;
